import axios from "axios";
import { DataTable } from "simple-datatables";
import { showAlert, showConfirm, handleRequestError } from "../../utils/alert";
import VMasker from "vanilla-masker"; // Importação do Vanilla Masker

export default () => ({
  // Modal States
  isModalOpen: false,
  isSubmitting: false,

  // Edit States
  editMode: false,
  editId: null,

  // Data Storage
  servicesList: [],

  // Form Payload
  serviceForm: {
    name: "",
    description: "",
    price: "", // Agora armazenará a string formatada "R$ 0,00"
    duration: 30,
    is_active: true,
  },

  // DataTable Instance
  servicesDataTable: null,

  async initServices() {
    const token = localStorage.getItem("jwt_token");
    if (!token) {
      window.location.href = "/client/login";
      return;
    }

    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    await this.loadServicesTable();
  },

  // ================= HELPERS DE FORMATAÇÃO =================

  formatDuration(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0) {
      return m > 0 ? `${h}h ${m}m` : `${h}h`;
    }
    return `${m} min`;
  },

  formatPrice(price) {
    return parseFloat(price).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  },

  // Aplica a máscara no input em tempo real
  applyMoneyMask(value) {
    if (!value) return "";
    // Remove tudo que não for número. Ex: de "45.00" (DB) vira "4500"
    const onlyNumbers = value.toString().replace(/\D/g, "");

    return VMasker.toMoney(onlyNumbers, {
      precision: 2,
      separator: ",",
      delimiter: ".",
      unit: "R$ ",
      zeroCents: false,
    });
  },

  // Evento disparado no Blade quando o utilizador digita
  handlePriceInput(event) {
    this.serviceForm.price = this.applyMoneyMask(event.target.value);
  },

  // Converte "R$ 45,00" de volta para Float "45.00" para enviar à API
  unmaskMoney(maskedValue) {
    if (!maskedValue) return 0;
    // Remove o "R$ ", troca os pontos de milhar por nada, e a vírgula decimal por ponto
    const cleanString = maskedValue
      .toString()
      .replace("R$ ", "")
      .replace(/\./g, "")
      .replace(",", ".");
    return parseFloat(cleanString);
  },

  // ================= DATA TABLE =================

  async loadServicesTable() {
    try {
      const response = await axios.get("/api/client/services");
      this.servicesList = response.data.data;

      if (this.servicesDataTable) {
        this.servicesDataTable.destroy();
      }

      const tbody = document.querySelector("#servicesTable tbody");
      tbody.innerHTML = this.servicesList
        .map((service) => {
          const statusBadge = service.is_active
            ? `<span class="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-bold text-green-700 ring-1 ring-inset ring-green-600/20">Ativo</span>`
            : `<span class="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-bold text-gray-600 ring-1 ring-inset ring-gray-500/10">Inativo</span>`;

          return `
            <tr>
              <td class="py-4 px-4 xl:px-6">
                <p class="font-bold text-slate-800">${service.name}</p>
                ${service.description ? `<p class="text-xs text-slate-500 mt-0.5 truncate max-w-xs">${service.description}</p>` : ""}
              </td>
              <td class="py-4 px-4 xl:px-6 font-medium text-slate-700">
                ${this.formatPrice(service.price)}
              </td>
              <td class="py-4 px-4 xl:px-6 text-slate-600">
                <div class="flex items-center gap-1.5">
                  <svg class="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  ${this.formatDuration(service.duration)}
                </div>
              </td>
              <td class="py-4 px-4 xl:px-6">
                ${statusBadge}
              </td>
              <td class="text-left py-4 px-4 xl:px-6">
                <div class="flex justify-start gap-2">
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('edit-service', {detail: ${service.id}}))"
                    class="inline-flex items-center justify-center rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    Editar
                  </button>
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('delete-service', {detail: ${service.id}}))"
                    class="inline-flex items-center justify-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600"
                  >
                    Excluir
                  </button>
                </div>
              </td>
            </tr>
          `;
        })
        .join("");

      this.servicesDataTable = new DataTable("#servicesTable", {
        searchable: false, // Reativei o search que é útil no catálogo
        perPageSelect: false,
        labels: {
          info: "Mostrando de {start} até {end} de {rows} serviços",
          noRows: "Nenhum serviço cadastrado",
          noResults: "Nenhum resultado encontrado",
          searchTitle: "Pesquisar serviço...",
        },
      });
    } catch (error) {
      console.error(error);
    }
  },

  // ================= MODAL HANDLERS =================

  openCreateService() {
    this.editMode = false;
    this.serviceForm = {
      name: "",
      description: "",
      price: "",
      duration: 30,
      is_active: true,
    };
    this.isModalOpen = true;
  },

  openEditService(id) {
    this.editMode = true;
    this.editId = id;
    const service = this.servicesList.find((s) => s.id === id);

    this.serviceForm = {
      name: service.name,
      description: service.description || "",
      // Ao editar, pegamos o "45.00" do banco e aplicamos a máscara para aparecer "R$ 45,00"
      price: this.applyMoneyMask(service.price),
      duration: service.duration,
      is_active: service.is_active,
    };
    this.isModalOpen = true;
  },

  // ================= SUBMIT =================

  async submitService() {
    this.isSubmitting = true;
    try {
      // Prepara os dados limpos para a API Laravel
      const apiPayload = {
        ...this.serviceForm,
        // Converte o "R$ 45,00" do ecrã de volta para o float 45.00 da API
        price: this.unmaskMoney(this.serviceForm.price),
      };

      if (this.editMode) {
        await axios.put(`/api/client/services/${this.editId}`, apiPayload);
        showAlert("Serviço atualizado com sucesso!", "success");
      } else {
        await axios.post("/api/client/services", apiPayload);
        showAlert("Serviço criado com sucesso!", "success");
      }

      await this.loadServicesTable();
      this.isModalOpen = false;
    } catch (error) {
      handleRequestError(error, "Erro ao guardar o serviço.");
    } finally {
      this.isSubmitting = false;
    }
  },

  // ================= DELETE =================

  async deleteService(id) {
    const result = await showConfirm(
      "Excluir Serviço",
      "Tem certeza que deseja apagar este serviço? Se ele já foi usado em agendamentos, recomendamos que você apenas o Desative (edite e desmarque a opção 'Ativo') para manter o histórico.",
    );

    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/client/services/${id}`);
        await this.loadServicesTable();
        showAlert("Serviço removido.", "success");
      } catch (error) {
        handleRequestError(error, "Erro ao excluir o serviço.");
      }
    }
  },
});
